import type { CurrencyISO, ISO2 } from '@/utilities/countries';
import type Company from '@simplywallst/services/lib/endpoints/types/company/Company';
import type { Statement as ResponseStatement } from '@simplywallst/services/lib/endpoints/types/company/general/CompanyAPIStatements';
import type { fetchCompany } from '../routines';

export type CompanySuccessPayload = ReturnType<
  typeof fetchCompany['success']
>['payload']['companyData'];

export interface State {
  urlToIdMapping: Record<string, string>;
  symbolToIdMapping: Record<string, string>;
  summary: Record<string, Summary>;
  statements: Record<string, Statements>;
  competitors: Record<string, string[]>;
  events: Record<string, Events>;
  news: Record<string, Record<string, string>>;
  formatting: Record<string, Formatting>;
  analysis: Record<string, AnalysisState>;
  management: Record<string, ManagementState>;
  insiderTransactions: Record<string, string[]>;
  insiderTransactionsMap: Record<string, InsiderTransaction>;
  listings: Record<string, Listing>;
  classificationStatus: Company['classification_status'];
  analysts: Record<
    string,
    {
      brokers: string[];
      analyst: string[];
    }
  >;
  topShareholders: Record<string, TopShareholders>;
}

export interface InsiderTransaction {
  filingDate: number | null;
  ownerObjectId: string;
  transactionType: string;
  transactionValue: number;
  ownerName: string;
  ownerType: string;
  shares: number;
  priceMax: number;
}

export interface Listing {
  primarySecurity: boolean;
  exchangeSymbol: string;
  tickerSymbol: string;
  securityName: string;
  exchangeName: string;
  exchangeCountryIso: string;
  currencyIso: string;
  startDate: null | number;
}

export interface People {
  id: string;
  salutation: string;
  firstName: string;
  lastName: string;
  age: number;
  yearBorn: number;
  shortTitle: string;
  /** Ca-ching */
  compensation: number;
  tenure: number;
  proTitle: string;
  biography: string;
  hasCEO: boolean;
  percentSharesOutstanding: string;
  sharesHeld: string;
}

export interface ManagementState {
  boardMembers: string[];
  managementTeam: string[];
  leaderId: string;
  boardAge: number;
  boardTenure: number;
  managementAge: number;
  managementTenure: number;
}

export interface Trade {
  avgPrice: number;
  totalShares: number;
  companyNames: string;
  individualNames: string;
  companyCount: number;
  uniqueOwners: number;
  individualCount: number;
  totalCompanyShares: number;
  totalIndividualShares: number;
}
export interface Transaction {
  filingDate: number;
  ownerObjectId: number;
  transactionType: string;
  transactionValue: number;
  ownerName: string;
  ownerType: string;
  shares: number;
  priceMax: number;
}
export interface AnalysisState {
  legacyChartData: {
    dividendEstimate: number[][];
    dividenEstimatesAnalystsAvg: number[][];
    historicalDividendPayments: number[][];
    historicalDividendYield: number[][];
    mergedFutureDividendsPerShare: number[][];
    mergedFutureYield: number[][];
    totalEmployees: number[][];

    insiderTrading: {
      totalSell: number;
      totalBuy: number;
      totalVolume: number;
      buy: Trade[];
      sell: Trade[];
    };
    generalAdministrativeExpense: number[][];
    futureEPS: number[][];
    futureEPSAnalyst: number[][];
    EPSAnalystConfirmation: {
      [date: number]: number;
    };
    futureEPSRange: number[][];
    futureRevenueEstimate: number[][];
    pastEPS: number[][];
    pastEPSRange: number[][];
    pastRevenueActuals: number[][];
    futureRevenueAnalysts: number[][];
    futureRevenueAnalystsMap: AnalystsMap;
    futureNetIncomeAnalystsMap: AnalystsMap;
    futureFreeCashflowAnalystsMap: AnalystsMap;
    futureCashflowAnalystsMap: AnalystsMap;
    futureNetIncome: number[][];
    pastNetIncome: number[][];
    futureCashFlow: number[][];
    pastCashFlow: number[][];
    pastFreeCashFlow: number[][];
    futureFreeCashFlow: number[][];
    revenueBestFit: number[][];
    netIncomeBestFit: number[][];
    pastNetIncomeBestFit: number[][];
    researchAndDevExpenses: number[][];
    totalDebtHistory: number[][];
    totalEquity: number[][];
    pastCashInvestments: number[][];
    healthOperatingExpensesTotal: number[][];
    ceoCompensationAnalysis: {
      salary: number[][];
      total: number[][];
    };
    ownershipBreakdown: {
      Institutions?: number;
      'Public Companies'?: number;
      'Private Companies'?: number;
      'Individual Insiders'?: number;
      'Company Controlled Foundation'?: number;
      'Employee Share Scheme'?: number;
      'State or Government'?: number;
      'Hedge Funds'?: number;
      'VC/PE Firms'?: number;
      'Sovereign Wealth Funds'?: number;
      'General Public'?: number;
    };
    uncappedPastEps: number[][];
  };
  forecastNetIncomeGrowth1y: number;
  forecastRevenueGrowth1y: number;
  beta5y: number;
  bookValuePerShare: number;
  cashStInvestments: number;
  earningsGrowthAnnual: number | null;
  earningsGrowthReq1Y: number;
  earningsPerShareGrowthAnnual: number;
  futureRevenueEstimateMax: number;
  futureROE3Y: number;
  intrinsicDiscount: number;
  intrinsicValue: number;
  pastRevenue: number;
  pastRevenueUnit: number;
  pastGrossProfit: number;
  pastGrossProfitUnit: number;
  pastGrossProfitMargin: number;
  pastEarnings: number;
  pastEarningsUnit: number;
  pastCostOfRevenue: number;
  pastCostOfRevenueUnit: number;
  pastBookValue: number;
  pastBookValueUnit: number;
  pastNetIncomeMargin: number;
  pastRevenueActualsMax: number;
  pastRevenueActualsMin: number;
  debtEquityRatio: number;
  valueRevenueRatio: number;
  valueEbitdaRatio: number;
  pastNetIncomeMax: number;
  pastNetIncomeMin: number;
  EPS: number;
  PB: number;
  PE: number;
  PEG: number;
  PS: number;
  preferredMultiple: string | null;
  preferredMultipleReason: string | null;
  preferredMultipleSecondary: string | null;
  preferredMultipleFairRatio: number | null;
  preferredMultipleAveragePeerValue: number | null;
  shouldShowAptCalc: boolean;
  return1YrAbs: number | null;
  return1YrTotalReturn: number;
  return30D: number | null;
  return3YrAbs: number | null;
  return3YrTotalReturn: number;
  return5YrAbs: number | null;
  return5YrTotalReturn: number | null;
  returnSinceIpoAbs: number | null;
  return7D: number | null;
  return90D: number | null;
  revenueGrowthAnnual: number | null;
  sharePrice: number;
  pastROE: number;
  pastROA: number;
  pastROCE1y: number;
  pastROCE3y: number;
  pastNetIncome: number;
  netIncomeGrowth1y: number | null;
  netIncomeGrowth5y: number | null;
  revenueGrowth1y: number;
  revenueGrowth5y: number;
  totalAssets: number;
  currentAssets: number;
  totalEquity: number;
  totalLiabEquity: number;
  totalCurrentLiab: number;
  receivables: number;
  inventory: number;
  PPE: number;
  /** Intrinsic Value raw data calculations */
  model: string;
  unleveredBeta: number;
  DE: number;
  taxRate: number;
  equityPremium: number;
  riskFreeRate: number;
  leveredBetaActual: number;
  leveredBeta: number;
  costOfEquity: number;
  adrPerShare: number;
  PV5Y: number;
  PVTV: number;
  terminalValue: number;
  NPV: number;
  NPVPerShare: number;
  NPVPerShareReportedCurrency: number;
  primaryCurrencyReported: number;
  primaryPrice: number;
  totalDebt: number;
  accountsPayable: number;
  dividendYield: number;
  dividendYieldFuture: number;
  dividendVolatility: boolean;
  dividendPayingYears: number;
  payoutRatio: number;
  payoutRatio3y: number;
  cashPayoutRatio: number;
  dividendYieldGrowthAnnual: number;
  firstPayment: number;
  lastPayment: number;
  upcomingDividend: null | {
    adjustmentFactor: number;
    amount: number;
    currencyId: number;
    date: number;
    nextDate: number;
    payDate: number;
    recordDate: number;
    splitAdjustedAmount: number;
  };
  marketCapRawData: {
    listing: number;
    primary: number;
    reported: number;
    usd: number;
    sharesOutstanding: number;
    relativeListingSharesOutstanding: number;
    originalMarketCap: number;
    totalEnterpriseValueReported: number;
  };
  /** TODO: needs to be flatten */
  REIT?: {
    NAV: number;
    NAVCount: number;
  };
  /** TODO: needs to be flatten */
  dividendDiscount?: {
    DPS: number;
    payout: number;
    ROE: number;
    expectedGrowth: number;
    DDMGrowth: number;
    DDMSource: string;
    NPVPerShare: number;
  };
  /** TODO: needs to be flatten */
  excessReturns?: {
    stableBookValue: number;
    returnOnEquityAvg: number;
    stableEPS: number;
    stableEPSSource: string;
    stableBookValueSource: string;
    bookValue: number;
    equityCost: number;
    excessReturn: number;
  };
  /** TODO: needs to be flatten */
  twoStageFCF?: {
    affoEstimates?: number;
    growthCAGR5y: number;
    scale: string;
    scaleNumeric: number;
    sharesOutstanding: number;
    fcfsource?: string;
    fcfData: { date: number; value: number }[];
    fcfCount: { date: number; value: number }[];
    fcfLtm: number;
    firstStage: {
      year: number;
      data: number;
      source: string;
      discounted: number;
    }[];
  };

  totalEmployees: number;
  lastEstimatesConfirmationTimeStamp: number;
  eodPriceUpdateTimeStamp: number;
  lastAnnualEarninsUpdateTimeStamp: number;
  lastEarningsUpdateTimeStamp: number;
  nextEarningsUpdateTimeStamp: number;
  lastFilingTimeStamp: number;
  minPrice52w: number;
  maxPrice52w: number;
  dailyReturnStdDev90d: number;
  forwardPE: number;
  forwardPS: number;
  forecastEarnings: number;
  forecastSales: number;
}

export interface Formatting {
  primaryTradingItemCurrencyISO: string;
  primaryTradingItemCurrencySymbol: string;
  reportingCurrencyISO: string;
  reportingCurrencySymbol: string;
  reportingUnit: number;
  reportingUnitAbs: number;
  reportingUnitText: string;
  reportingUnitTextAbs: string;
  tradingItemCurrencyISO: string;
  tradingItemCurrencySymbol: string;
  isExchangeRateNeeded: boolean;
  exchangeRate: number;
  secondaryListingExchangeRate: number;
}

export interface Summary {
  mainHeader: string;
  mainThumb: string;
  isSearchable: boolean;
  lastAnalysisUpdate: number;
  address1: string;
  address2: string;
  analystCount: number;
  beta: number;
  betaIndustry: number;
  canonicalUrl: string;
  classificationStatus: Company['classification_status'];
  city: string;
  companyId: string;
  country: string;
  countryISO: '' | ISO2;
  currencyISO: '' | CurrencyISO;
  currencySymbol: string;
  description: string; // use .info.companyInfo.description
  exchangeCountryISO: '' | ISO2;
  exchangeSymbol: string;
  exchangeSymbolFiltered: string;
  exchangeTicker: string;
  primaryListingExchangeSymbol: string;
  primaryListingTickerSymbol: string;
  listings: string[];
  /** Fundamental sentence */
  fundamentals: string;
  industryId: string;
  industryAvergageId: string;
  isFund: boolean;
  name: string;
  postCode: string; // renamed from .info.zipCode
  primaryCanonicalUrl: string;
  refreshTime: number;
  restricted: boolean;
  score: {
    future: number;
    health: number;
    income: number;
    management: number;
    misc: number;
    past: number;
    total: number;
    value: number;
  };
  shortDescription: string; // use summary.description
  shortName: string;
  shortNamePlural: string;
  slug: string;
  snowflakePoints: number[];
  state: string;
  tickerSymbol: string;
  totalEmployeesFigure: string;
  uniqueSymbol: string;
  website?: string; // renamed from .info.url
  yearFounded: number;
  industryData: {
    primaryId: string;
    primaryName: string;
    secondaryId: string;
    secondaryName: string;
    tertiaryId: string;
    tertiaryName: string;
  };
  totalRiskStatements: number;
  return7D: number | null;
  return1YrAbs: number | null;
  marketCap: number | null;
  sharePrice: number | null;
  analystsPriceTarget: number | null;
  pb: number | null;
  pe: number | null;
  ps: number | null;
  growthEstimate: number | null;
  earningsGrowth: number | null;
  salesGrowth: number | null;
  dividendYield: number | null;
  logoUrl: string;
}
export type StatementValue = 'Pass' | 'Fail' | 'No data' | 'UnInitialised';
export type Context = Record<string, string>;
export type Severity = 'minor' | 'major' | 'none';
export type Area =
  | 'BankHealth'
  | 'Dividends'
  | 'Future'
  | 'Health'
  | 'Management'
  | 'Misc'
  | 'Past'
  | 'Risks'
  | 'Value';

export interface Statement {
  date: number | null;
  title: string;
  area: Area | null;
  type: ResponseStatement['type'];
  value: StatementValue;
  outcome: number | null;
  outcomeName: string;
  severity: Severity;
  description: string;
  context: Context;
  question: string;
  url: { href: string; label: string } | null;
}

export interface AnalystsMap {
  [date: number]: [number, number];
}

// export interface Context {
//   low?: string;
//   significantly?: string;
//   experienced?: string;
//   high?: string;
//   volatile?: string;
//   wellCovered?: string;
//   poorValue?: string;
// }

export type Statements = Record<string, Statement>;

export interface SelectedStatement {
  title: string;
  description: string;
  formattedDescription: string;
  context: Context;
  value: StatementValue;
  severity: Severity;
  question: string;
  outcomeName: string;
  url: {
    href: string;
    label: string;
  } | null;
}

export interface SelectorProps {
  companyId?: string;
}

export interface Valuation {
  intrinsicValue: number;
  sharePrice: number;
  intrinsicDiscount: number;
  PE: number;
  PB: number;
  PEG: number;
  return1YrAbs: number;
  return1YrTotalReturn: number;
  return3YrAbs: number;
  return3YrTotalReturn: number;
  return5YrAbs: number;
  return5YrTotalReturn: number;
  return7D: number;
  return30D: number;
  return90D: number;
  beta5y: number;
}

export type Mark = 'PASS' | 'FAIL';

export interface AnalysisCheck {
  mark: Mark;
  hash?: string;
  title: string;
  description: string;
}

export enum SnowflakeIndices {
  VALUE,
  FUTURE,
  PAST,
  HEALTH,
  DIVIDEND,
}

export interface CompanyTopShareholders {
  data: Shareholder[];
}

export type TopShareholders = Record<string, Shareholder>;

export interface Shareholder {
  rankSharesHeld: number;
  owner: Owner;
  holdingDate: string;
  sharesHeld: number;
  percentOfPortfolio: number;
  percentOfSharesOutstanding: number;
  sharesChanged: number;
  percentSharesChanged: number;
  rankSharesBought: number;
  rankSharesSold: number;
  periodStartDate: number;
  periodEndDate: number;
}

export interface Owner {
  name: string;
  type: OwnerType;
  companyType: number;
  objectId: number;
}

export enum OwnerType {
  Company = 'Company',
  Individual = 'Individual',
}

export interface Events {
  keys: number[];
  entities: {
    [key: number]: string[];
  };
  categoryKeys: string[];
}
